import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const UploadContainer = styled.div`
  width: 100%;
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.isDragOver ? '#007AFF' : '#ddd'};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background: ${props => props.isDragOver ? '#f0f8ff' : '#fafafa'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: #007AFF;
    background: #f0f8ff;
  }
  
  ${props => props.hasImage && `
    padding: 0;
    border: none;
    background: transparent;
    cursor: default;
    
    &:hover {
      background: transparent;
    }
  `}
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  
  .upload-icon {
    font-size: 48px;
    color: #007AFF;
  }
  
  .upload-text {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
  }
  
  .upload-hint {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
  }
`;

const ImagePreview = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f0f0f0;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
  }
  
  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 1;
    }
  }
  
  .overlay-button {
    background: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    margin: 0 8px;
    
    &:hover {
      background: #f0f0f0;
    }
    
    &.danger {
      background: #dc3545;
      color: white;
      
      &:hover {
        background: #c82333;
      }
    }
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #007AFF;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
`;

const ImageUpload = ({ 
  value, 
  onChange, 
  maxSizeKB = 2048, // 2MB default
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  placeholder = "Upload Image",
  uploadEndpoint = '/api/upload/image'
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    setError('');
    
    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    
    // Validate file size
    if (file.size > maxSizeKB * 1024) {
      setError(`Image must be smaller than ${maxSizeKB/1024}MB.`);
      return;
    }
    
    try {
      setUploading(true);
      
      // Create FormData for upload
      const formData = new FormData();
      formData.append('image', file);
      
      // Get auth token for upload
      const token = localStorage.getItem('userToken');
      
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const result = await response.json();
      
      // Call onChange with the uploaded image URL
      if (onChange) {
        onChange(result.imageUrl);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleClick = () => {
    if (!value && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleReplace = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    if (onChange) {
      onChange(null);
    }
    setError('');
  };

  return (
    <UploadContainer>
      <UploadArea
        isDragOver={isDragOver}
        hasImage={!!value}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        {uploading && (
          <LoadingOverlay>
            <div className="spinner"></div>
          </LoadingOverlay>
        )}
        
        {value ? (
          <ImagePreview>
            <img src={value} alt="Uploaded" />
            <div className="image-overlay">
              <button className="overlay-button" onClick={(e) => {
                e.stopPropagation();
                handleReplace();
              }}>
                Replace
              </button>
              <button className="overlay-button danger" onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}>
                Remove
              </button>
            </div>
          </ImagePreview>
        ) : (
          <UploadContent>
            <div className="upload-icon">📷</div>
            <div className="upload-text">{placeholder}</div>
            <div className="upload-hint">
              Drag & drop or click to upload<br/>
              JPEG, PNG, or WebP (max {maxSizeKB/1024}MB)
            </div>
          </UploadContent>
        )}
      </UploadArea>
      
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileSelect}
      />
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </UploadContainer>
  );
};

export default ImageUpload;